import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { I18nextPhraseBackend } from '@phrase/i18next-backend';
import * as PusherPushNotifications from '@pusher/push-notifications-web';
import * as Sentry from '@sentry/vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import MasonryWall from '@yeger/vue-masonry-wall';
import Chocolat from 'chocolat';
import i18next from 'i18next';
import I18NextVue from 'i18next-vue';
import Echo from 'laravel-echo';
import { getActiveLanguage } from 'laravel-vue-i18n';
import LaravelVuePagination from 'laravel-vue-pagination';
import _ from 'lodash';
import Maska from 'maska';
import moment from 'moment';
import Pusher from 'pusher-js';
import 'swiper/scss';
import 'swiper/scss/grid';
import 'swiper/scss/navigation';
import 'swiper/scss/pagination';
import resolveConfig from 'tailwindcss/resolveConfig';
import { createApp } from 'vue';
import VueCookies from 'vue-cookies';
import VuePlyr from 'vue-plyr';
import 'vue-plyr/dist/vue-plyr.css';
import VueSocialSharing from 'vue-social-sharing';
import twitter from 'vue-twitter';
import VueZoomer from 'vue-zoomer';
import Popper from 'vue3-popper';
import tailwindConfig from '../tailwind.config.cjs';
import App from './App.vue';
import api from './api';
import ClickOutside from './directives/click-outside.js';
import './font-awesome.js';
import {
    asset,
    countLines,
    currencyData,
    dragElement,
    formatDateByRegion,
    formatDateTimeByRegion,
    getCurrencyCode,
    getCurrencySymbol,
    getRegion,
    hexToRgbA,
    isOriginalCurrency,
    meta,
    onScreenPercentage,
    scrollToId,
    shuffleArray,
    toCurrency,
    toPercent
} from './helpers';
import './registerServiceWorker.js';
import router from './router';
import { store } from './store';
import { toast } from './toast';
import { Ccm19Plugin } from './useCcm19Global.js';

window.Pusher = Pusher;
window.Chocolat = Chocolat;
window._ = _;
const isLocalhost = ['localhost', '127.0.0.1', 'www.hkcm.test'].includes(window.location.hostname);

// Create App
const app = createApp(App);

// initialize Sentry Loggin

if (!isLocalhost) {
    Sentry.init({
        app,
        dsn: 'https://1beeb91f645cb74543bc2eff2e7e895f@o4507101962240000.ingest.de.sentry.io/4508335695528016',
        integrations: [Sentry.browserTracingIntegration({ router })],
        // Tracing
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ['localhost', /^https:\/\/hkcmanagement\.de\/.*/],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

// Require components
const requiredComponents = import.meta.glob('./components/**/*.vue', { eager: true });

for (const key in requiredComponents) {
    const componentName = key
        .split('/')
        .pop()
        .replace(/\.\w+$/, '');

    app.component(componentName, requiredComponents[key].default || requiredComponents[key]);
}
//

// load selected currency from localStorage or query parameter BEFORE loading rates
const queryCurrency = new URLSearchParams(window.location.search).get('selectedCurrency');
if (queryCurrency !== null && queryCurrency !== undefined) {
    window.meta.currency = queryCurrency;
} else if (localStorage.getItem('selectedCurrency')) {
    window.meta.currency = localStorage.getItem('selectedCurrency');
}

// load currency exchange rates before site is loaded
try {
    const base = getCurrencySymbol(true);
    const response = await fetch(`/currency-rates/${base}`);
    const data = await response.json();
    const rates = data.rates;

    // update currency rates
    Object.keys(currencyData).forEach((currencyCode) => {
        if (currencyCode === base) {
            currencyData[currencyCode].rate = 1; // base currency rate is always 1
        } else if (rates[currencyCode]) {
            currencyData[currencyCode].rate = rates[currencyCode];
        }
    });
} catch (error) {
    console.error('Failed to load currency exchange rates', error);
}

// Set locale for time
moment.locale(window.meta.region_configuration.language);
app.config.globalProperties.$moment = moment;

// Helper functions for dynamic translations
app.config.globalProperties.$translate = (attribute) => {
    if (typeof attribute === 'object' && !Array.isArray(attribute) && attribute !== null) {
        // object
        if (attribute[getActiveLanguage()]) {
            return attribute[getActiveLanguage()];
        } else {
            return attribute[app.config.globalProperties.fallbackLanguage];
        }
    } else if (Array.isArray(attribute) || attribute === null) {
        // array or null
        return null;
    } else {
        // if none of all just return the value
        return attribute;
    }
};

app.config.globalProperties.$tw = resolveConfig(tailwindConfig);
app.config.globalProperties.$toPercent = toPercent;
app.config.globalProperties.$formatDateByRegion = formatDateByRegion;
app.config.globalProperties.$formatDateTimeByRegion = formatDateTimeByRegion;
app.config.globalProperties.$getRegion = getRegion;
app.config.globalProperties.$currencyData = currencyData;
app.config.globalProperties.$toCurrency = toCurrency;
app.config.globalProperties.$getCurrencySymbol = getCurrencySymbol;
app.config.globalProperties.$isOriginalCurrency = isOriginalCurrency;
app.config.globalProperties.$getCurrencyCode = getCurrencyCode;
app.config.globalProperties.$toast = toast;
app.config.globalProperties.$scrollToId = scrollToId;
app.config.globalProperties.$meta = meta;
app.config.globalProperties.$hexToRgbA = hexToRgbA;
app.config.globalProperties.$asset = asset;
app.config.globalProperties.$api = api;
app.config.globalProperties.$onScreenPercentage = onScreenPercentage;
app.config.globalProperties.$countLines = countLines;
app.config.globalProperties.$shuffleArray = shuffleArray;
app.config.globalProperties.$dragElement = dragElement;
app.config.globalProperties.$isCheckoutAllowed = import.meta.env.VITE_CHECKOUT_ALLOWED === 'true' || localStorage.getItem('overrideCheckoutAllowed');
app.config.globalProperties.$showLanguageSwitch = import.meta.env.VITE_SHOW_LANGUAGE_SWITCH === 'true' || localStorage.getItem('overrideShowLanguageSwitch');

app.config.globalProperties.getQueryParameterByName = (name) => {
    return new URLSearchParams(window.location.search).get(name);
};

app.config.globalProperties.removeQueryParameterByName = (name, value = undefined) => {
    new URLSearchParams(window.location.search).delete(name);
};

(app.config.globalProperties.replaceQueryParams = (params) => {
    const url = new URL(window.location);

    _.forEach(params, (param, key) => {
        param != '' ? url.searchParams.set(key, param) : url.searchParams.delete(key);
    });

    window.history.pushState({}, '', url);
}),
    // // Check if user already subscribed to channel
    (app.config.globalProperties.$subscribedToChannel = (channel) => {
        return app.config.globalProperties.$echo.connector.channels.hasOwnProperty(channel);
    });

// // Config and set Pusher Client
app.config.globalProperties.$echo = new Echo({
    broadcaster: 'pusher',
    key: 'app-key',
    wsHost: 'ws.hkcmanagement.de',
    wsPort: '443',
    wssPort: '443',
    forceTLS: false,
    encrypted: false,
    disableStats: true,
    enabledTransports: ['ws', 'wss']
});

// // Config and set Beams Client
if (window.meta.beams_instance_id) {
    if ('Notification' in window && 'PushManager' in window && window.location.protocol === 'https:') {
        app.config.globalProperties.$beams = window.$beams = new PusherPushNotifications.Client({
            instanceId: window.meta.beams_instance_id
        });

        app.config.globalProperties.$beamsTokenProvider = window.$beamsTokenProvider = new PusherPushNotifications.TokenProvider({
            url: '/api/v1/beams/token'
        });
    }
}

app.use(I18NextVue, { i18next });
app.use(VueSocialSharing);
app.use(VuePlyr);
app.use(store);
app.use(router);
app.use(MasonryWall);
app.use(Maska);
app.use(ClickOutside);
app.use(_);
app.use(VueCookies);
app.use(twitter);
app.use(VueZoomer);
app.use(Ccm19Plugin);
app.component('Pagination', LaravelVuePagination);
app.component('FontAwesomeIcon', FontAwesomeIcon);
app.component('Datepicker', Datepicker);
app.component('Popper', Popper);

/--------------------- START COOKIE CCM19 ---------------------/;

// CCM19 → Consent-Objekt
/* const getConsentFromCCM = () => {
    const CCM = window.CCM;

    const defaultConsent = {
        advertising: false,
        functional: false,
        necessary: true,
        performance: false
    };

    if (!CCM) return defaultConsent;

    if (CCM.fullConsentGiven === true) {
        return {
            advertising: true,
            functional: true,
            necessary: true,
            performance: true
        };
    }

    return defaultConsent;
};

// Helper
const pushConsentToStore = (retry = 0) => {
    const CCM = window.CCM;

    if (CCM && typeof CCM.fullConsentGiven === 'undefined' && retry < 20) {
        setTimeout(() => pushConsentToStore(retry + 1), 200);

        return;
    }

    const consent = getConsentFromCCM();
    console.log('Consent aus CCM19:', consent);
    store.dispatch('consent/set', consent);
};

// Initial Laden
if (document.readyState === 'complete') {
    pushConsentToStore();
} else {
    window.addEventListener('load', () => {
        pushConsentToStore();
    });
}

// CCM19 Events
window.addEventListener('ccm19WidgetClosed', () => pushConsentToStore());
window.addEventListener('ccm19EmbeddingAccepted', () => pushConsentToStore());
window.addEventListener('ccm19CookieAccepted', () => pushConsentToStore()); */

/--------------------- END COOKIE CCM19 ---------------------/;

i18next
    .use(I18nextPhraseBackend)
    .init({
        lng: window.meta.region_configuration.language,
        fallbackLng: window.meta.region_configuration.fallback_language,
        debug: true,
        backend: {
            debug: true,
            distribution: import.meta.env.VITE_PHRASE_OTA_DIST_ID,
            secret: import.meta.env.VITE_PHRASE_OTA_SECRET,
            appVersion: '1.0.0',
            cacheExpirationTime: 60 * 5 // time in seconds
        }
    })
    .then(() => app.mount('#app'));

// prevent right click on all images
window.addEventListener(
    'contextmenu',
    function (event) {
        if (event.target.nodeName == 'IMG' && event.target.classList.contains('prevent-click')) {
            event.preventDefault();
        }
    },
    false
);

window.addEventListener(
    'touchstart',
    function (event) {
        if (event.target.nodeName == 'IMG' && event.target.classList.contains('prevent-click')) {
            event.preventDefault();
        }
    },
    false
);

window.addEventListener(
    'touchmove',
    function (event) {
        if (event.target.nodeName == 'IMG' && event.target.classList.contains('prevent-click')) {
            event.preventDefault();
        }
    },
    false
);

window.addEventListener(
    'click',
    function (event) {
        if (event.target.nodeName == 'IMG' && event.target.classList.contains('prevent-click')) {
            event.preventDefault();
        }
    },
    false
);
